import {
  getClientConfigRuntime,
  type ClientConfigRuntime,
} from './client-config-runtime'

/** Env keys mapped to runtime config field names (after `main()` sets runtime from client config). */
const RUNTIME_CONFIG_KEY_MAP: Partial<
  Record<string, keyof ClientConfigRuntime>
> = {
  APP_ENV: 'appEnv',
  VERSION: 'version',
  VITE_LOG_LEVEL: 'logLevel',
  VITE_TERMS_URL: 'termsUrl',
  VITE_PRIVACY_URL: 'privacyUrl',
  VITE_SHOPIFY_APP_HANDLE: 'shopifyAppHandle',
}

/**
 * Environment variable helper. When runtime client config is set (by `main()` after fetchClientConfig), returns values from it for known keys.
 * Otherwise prefers Vite `import.meta.env`, then falls back to `process.env` when the key is not in the Vite env object.
 */
export const getEnvVar = ({ key }: { key: string }): string | undefined => {
  const runtime = getClientConfigRuntime()
  const configKey = RUNTIME_CONFIG_KEY_MAP[key]
  if (runtime && configKey && configKey in runtime) {
    const v = runtime[configKey]
    return v !== undefined && v !== null ? String(v) : undefined
  }

  const importMetaEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta.env as Record<string, string | boolean | undefined>)
      : undefined
  if (importMetaEnv != null && key in importMetaEnv) {
    const value = importMetaEnv[key]
    return value !== undefined && value !== false ? String(value) : undefined
  }

  if (typeof process !== 'undefined' && process.env[key] !== undefined) {
    return process.env[key]
  }

  return undefined
}

/** App environment (development | stage | production). From runtime config or build/env. */
export const getAppEnv = (): string =>
  getEnvVar({ key: 'APP_ENV' }) ?? 'development'

/** App version (e.g. from deployment). Used for Sentry release. */
export const getVersion = (): string | undefined =>
  getEnvVar({ key: 'VERSION' }) ?? undefined
